'use client';

import type { ComponentProps } from 'react';
import { createContext, use, useState } from 'react';
import { useIsClient } from 'usehooks-ts';

import { SidebarProvider } from '@/components/ui/sidebar';
import { PR_V2_RIGHT_SIDEBAR_COOKIE_NAME } from '@/lib/stores/pr-v2-view-preferences';
import { usePrV2ViewPreferencesStore } from '@/lib/stores/pr-v2-view-preferences-store';

// The element the diff portals the group card into. The chat column mounts it only while the
// column is open on a desktop screen. null (no chat column, or it is closed): the diff keeps its
// guides beside each group.
export const PrV2GroupCardSlotContext = createContext<HTMLElement | null>(null);
const SetPrV2GroupCardSlotContext = createContext<(slot: HTMLElement | null) => void>(() => {});

export function usePrV2GroupCardSlot() {
  return use(PrV2GroupCardSlotContext);
}

export function useSetPrV2GroupCardSlot() {
  return use(SetPrV2GroupCardSlotContext);
}

type PrV2SidebarProviderProps = Omit<
  ComponentProps<typeof SidebarProvider>,
  'open' | 'onOpenChange' | 'openCookieName' | 'hasChat'
> & {
  alwaysOpen?: boolean;
};

export function PrV2SidebarProvider({
  alwaysOpen = false,
  defaultOpen = true,
  children,
  ...props
}: PrV2SidebarProviderProps) {
  const isClient = useIsClient();
  const rightSidebarOpen = usePrV2ViewPreferencesStore((state) => state.rightSidebarOpen);
  const setRightSidebarOpen = usePrV2ViewPreferencesStore((state) => state.setRightSidebarOpen);
  const open = alwaysOpen || (isClient ? rightSidebarOpen : undefined);
  const onOpenChange = alwaysOpen || !isClient ? undefined : setRightSidebarOpen;
  const [groupCardSlot, setGroupCardSlot] = useState<HTMLElement | null>(null);

  return (
    <SidebarProvider
      defaultOpen={defaultOpen}
      // Keep the server-rendered default for the first paint, then hand control
      // to the session store once the browser is available.
      open={open}
      onOpenChange={onOpenChange}
      openCookieName={PR_V2_RIGHT_SIDEBAR_COOKIE_NAME}
      // The PR layout renders the chat in this sidebar (PrV2SidebarRight).
      hasChat
      {...props}
    >
      <SetPrV2GroupCardSlotContext value={setGroupCardSlot}>
        <PrV2GroupCardSlotContext value={groupCardSlot}>{children}</PrV2GroupCardSlotContext>
      </SetPrV2GroupCardSlotContext>
    </SidebarProvider>
  );
}
